import { useState, useMemo, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import FilterBar from '../components/FilterBar';
import { THEME_META } from '../data/themes';
// DEMO_DATA_REMOVED — THEME_POETS removed; poet filter built dynamically from DB poems
import { POET_EN_MAP } from '../data/path-poems';
import { readUserLevel } from '../lib/theme-recommendations';
import { useThemePoems } from '../hooks/use-theme-poems';
import { useUserProgress } from '../hooks/use-user-progress';

function lengthLabel(bayts) {
  if (bayts <= 10)  return 'Short';
  if (bayts <= 25)  return 'Medium';
  return 'Long';
}

function poetLabel(p) { return p.poetEn || POET_EN_MAP[p.poetAr] || p.poetAr || 'Unknown'; }

// ── PoemCard ──────────────────────────────────────────────────────────────────

const PoemCard = memo(function PoemCard({ poem, onPress }) {
  return (
    <div
      className="featured-card"
      onClick={onPress}
      style={{ marginBottom: '0.75rem', padding: '1.1rem 1.25rem' }}
    >
      <div className="featured-card-glow" />

      {/* Tags row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          <span className="tag tag-gold">{poem.level}</span>
          <span className="tag">{poem.era}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          <span className="tag">{lengthLabel(poem.bayts)}</span>
          <span className="tag">{poem.bayts} b</span>
        </div>
      </div>

      {/* Arabic excerpt */}
      <div style={{
        fontFamily: "'Noto Naskh Arabic', serif",
        fontSize: '1.05rem', lineHeight: 1.9,
        direction: 'rtl', textAlign: 'center',
        color: 'var(--text)', marginBottom: '0.3rem', padding: '0.35rem 0',
      }}>
        {poem.excerpt}
      </div>

      {/* Title — English if available, otherwise Arabic */}
      <div style={{
        fontSize: '0.72rem', color: 'var(--text3)',
        fontStyle: poem.titleEn ? 'italic' : 'normal',
        fontFamily: poem.titleEn ? 'inherit' : "'Noto Naskh Arabic', serif",
        direction: poem.titleEn ? 'ltr' : 'rtl',
        textAlign: 'center', marginBottom: '0.65rem',
      }}>
        {poem.titleEn ? `"${poem.titleEn}"` : poem.titleAr}
      </div>

      {/* Poet row + progress */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '0.82rem', color: 'var(--gold2)', fontStyle: 'italic' }}>
            {poem.poetEn || poem.poetAr}
          </div>
          <div style={{
            fontFamily: "'Noto Naskh Arabic', serif",
            fontSize: '0.78rem', color: 'var(--text3)', direction: 'rtl',
          }}>
            {poem.poetAr}
          </div>
        </div>
        {poem.pct !== null ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <div style={{ width: 38 }}>
              <div className="path-bar">
                <div className="path-bar-fill" style={{ width: `${poem.pct}%` }} />
              </div>
            </div>
            <span className="path-pct">{poem.pct}%</span>
          </div>
        ) : (
          <span className="fc-cta">Study →</span>
        )}
      </div>
    </div>
  );
});

// ── Main screen ───────────────────────────────────────────────────────────────

export default function ThemeDetail() {
  const { themeId }  = useParams();
  const navigate     = useNavigate();
  const userId = null;
  const [filterType,  setFilterType]  = useState('Difficulty');
  const [filterValue, setFilterValue] = useState('All');
  const [navError,    setNavError]    = useState(false);

  const meta      = THEME_META[themeId];
  const userLevel = useMemo(() => readUserLevel(), []);

  const { poems: rawPoems, total: totalPoems, error: fetchError, loadingMore, loadMore } = useThemePoems(themeId);
  const progressMap = useUserProgress(userId);

  const dbPoems = rawPoems; // alias kept for loading-state checks below

  // Merge user progress: STUDIED → 100%, else null (shows "Study →")
  // rawPoems is null while loading; guard with ?? [] so useMemo never calls .map on null
  const poems = useMemo(() =>
    (rawPoems ?? []).map(p => ({ ...p, pct: progressMap[p.id] === 'STUDIED' ? 100 : null })),
    [rawPoems, progressMap]
  );

  // ── Build filter options dynamically from this theme's poems ──
  const filterOptions = useMemo(() => {
    const poets = ['All', ...new Set(poems.map(poetLabel).filter(Boolean))];
    const eras  = ['All', ...new Set(poems.map(p => p.era).filter(Boolean))];
    const LEVELS_PRESENT = ['A2', 'B1', 'B2', 'C1'].filter(l => poems.some(p => p.level === l));
    const LENGTHS_PRESENT = ['Short', 'Medium', 'Long'].filter(l =>
      poems.some(p => lengthLabel(p.bayts) === l)
    );
    return {
      Poet:       poets,
      Era:        eras,
      Difficulty: ['All', ...LEVELS_PRESENT],
      Length:     ['All', ...LENGTHS_PRESENT],
    };
  }, [poems]);

  // ── Filtered poems ──
  const isFiltered = filterValue !== 'All';
  const filtered   = useMemo(() => {
    if (!isFiltered) return poems;
    if (filterType === 'Poet')       return poems.filter(p => poetLabel(p) === filterValue);
    if (filterType === 'Era')        return poems.filter(p => p.era === filterValue);
    if (filterType === 'Difficulty') return poems.filter(p => p.level === filterValue);
    if (filterType === 'Length')     return poems.filter(p => lengthLabel(p.bayts) === filterValue);
    return poems;
  }, [filterType, filterValue, isFiltered, poems]);

  // ── Recommended for You ──
  const recommended = useMemo(() => {
    const matches = poems.filter(p => p.level === userLevel);
    return (matches.length > 0 ? matches : poems).slice(0, 2);
  }, [poems, userLevel]);

  function goPoem(poem) {
    if (!poem.id) { setNavError(true); return; }
    try {
      navigate(`/poem/${poem.id}`);
    } catch {
      setNavError(true);
    }
  }

  // ── Not found guard ──
  if (!meta) {
    return (
      <div className="phone-shell">
        <div className="accent-bar" />
        <div className="screen-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 1.5rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔍</div>
            <div style={{ color: 'var(--text3)', fontStyle: 'italic', marginBottom: '1rem' }}>
              This path couldn't be found.
            </div>
            <button className="back-btn" onClick={() => navigate('/all-paths')}>
              ← Back to All Paths
            </button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="phone-shell">
      <div className="accent-bar" />

      <div className="screen-content">

        {/* ── Header ── */}
        <div style={{ padding: '2.5rem 1.5rem 1.25rem', position: 'relative', zIndex: 1 }}>

          <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginTop: '0.6rem' }}>
            {/* Theme icon */}
            <div style={{
              width: 52, height: 52, borderRadius: 6, flexShrink: 0,
              background: 'rgba(140,207,225,0.1)',
              border: '1px solid rgba(140,207,225,0.22)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.5rem',
            }}>
              {meta.icon}
            </div>

            <div>
              {/* Arabic name */}
              <div style={{
                fontFamily: "'Noto Naskh Arabic', serif",
                fontSize: '1.3rem', color: 'var(--gold)',
                direction: 'rtl', lineHeight: 1.3, marginBottom: '0.1rem',
              }}>
                {meta.nameAr}
              </div>
              {/* English name */}
              <div style={{ fontSize: '1.2rem', color: 'var(--text)', fontStyle: 'italic' }}>
                {meta.nameEn}
              </div>
              {/* Meta line */}
              <div style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                color: 'var(--text3)', marginTop: '0.25rem',
              }}>
                {dbPoems === null ? 'Loading…' : `${totalPoems || poems.length} poems`} · {meta.level} recommended · Your level: {userLevel}
              </div>
            </div>
          </div>

          {/* Description */}
          <div style={{
            fontSize: '0.78rem', color: 'var(--text3)', lineHeight: 1.65,
            fontStyle: 'italic', marginTop: '0.85rem',
            borderLeft: '2px solid var(--border)', paddingLeft: '0.75rem',
          }}>
            {meta.desc}
          </div>
        </div>

        <div style={{ padding: '0 1.5rem 2rem', position: 'relative', zIndex: 1 }}>

          {/* ── Fetch error banner ── */}
          {fetchError && (
            <div role="alert" style={{ fontSize: '0.75rem', color: 'rgba(239,68,68,0.8)', marginBottom: '0.75rem', padding: '0.6rem 0.85rem', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 4 }}>
              These verses are out of reach — check your connection and refresh.
            </div>
          )}

          {/* ── Filter bar ── */}
          <FilterBar
            types={['Poet', 'Era', 'Difficulty', 'Length']}
            options={filterOptions}
            filterType={filterType}   setFilterType={setFilterType}
            filterValue={filterValue} setFilterValue={setFilterValue}
          />

          {/* ── Error fallback ── */}
          {navError && (
            <div role="alert" style={{
              fontSize: '0.75rem', color: 'rgba(239,68,68,0.8)',
              marginBottom: '0.75rem', padding: '0.6rem 0.85rem',
              border: '1px solid rgba(239,68,68,0.25)', borderRadius: 4,
            }}>
              Couldn't open this poem — try again.
            </div>
          )}

          {/* ── Recommended for You (hidden when filtered or still loading) ── */}
          {!isFiltered && dbPoems !== null && (
            <div style={{ marginBottom: '1.75rem' }}>
              <div className="home-section-title">
                <span>Recommended for You</span>
                <span className="tag tag-em">{userLevel}</span>
              </div>
              {recommended.map(p => (
                <PoemCard key={p.id} poem={p} onPress={() => goPoem(p)} />
              ))}
            </div>
          )}

          {/* ── All / filtered poems ── */}
          <div>
            <div className="home-section-title">
              <span>{isFiltered ? `${filterType}: ${filterValue}` : 'All Poems'}</span>
              <span style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.58rem', letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'var(--text3)',
              }}>
                {filtered.length} poem{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>

            {dbPoems === null ? (
              <div style={{
                textAlign: 'center', color: 'var(--text3)',
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.6rem', letterSpacing: '0.15em',
                textTransform: 'uppercase', marginTop: '3rem',
              }}>
                Turning the pages…
              </div>
            ) : filtered.length === 0 ? (
              <div style={{
                textAlign: 'center', color: 'var(--text3)',
                fontSize: '0.85rem', marginTop: '2rem',
                fontStyle: 'italic', lineHeight: 1.75,
              }}>
                {isFiltered ? 'No poems match — try a different filter.' : 'No poems found for this theme.'}
                <br />
                {isFiltered && (
                  <button
                    onClick={() => setFilterValue('All')}
                    style={{
                      marginTop: '0.75rem', background: 'none',
                      border: '1px solid var(--border)', borderRadius: 3,
                      padding: '0.45rem 1rem',
                      fontFamily: "'Josefin Sans', sans-serif",
                      fontSize: '0.6rem', letterSpacing: '0.12em',
                      textTransform: 'uppercase', color: 'var(--em)', cursor: 'pointer',
                    }}
                  >
                    Clear filter
                  </button>
                )}
              </div>
            ) : (
              <>
                {filtered.map(p => (
                  <PoemCard key={p.id} poem={p} onPress={() => goPoem(p)} />
                ))}
                {!isFiltered && totalPoems !== null && dbPoems !== null && dbPoems.length < totalPoems && (
                  <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      style={{
                        background: 'none', border: '1px solid var(--border)', borderRadius: 3,
                        padding: '0.5rem 1.25rem', fontFamily: "'Josefin Sans', sans-serif",
                        fontSize: '0.6rem', letterSpacing: '0.12em', textTransform: 'uppercase',
                        color: 'var(--em)', cursor: loadingMore ? 'default' : 'pointer',
                        opacity: loadingMore ? 0.5 : 1,
                      }}
                    >
                      {loadingMore ? 'Loading…' : `Load more (${totalPoems - dbPoems.length} remaining)`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>

      <BottomNav />
    </div>
  );
}
